import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Stack,
  Box,
  Button,
  Divider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
  TextField,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Input,
} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import CloseIcon from "@mui/icons-material/Close";
import { Loader, Dimmer } from "semantic-ui-react";
import { ReactMultiEmail, isEmail } from "react-multi-email";

import { client_mode, classification_id } from "constants/client_properties";
import { SYSTEM, AGENCY, getVendorLabel, getProjectLabel } from "constants/organization_types";
import cognito_groups from "constants/cognito_groups";
import DisplayProjectCoverages from "components/shared/DisplayProjectCoverages";
import { SideMenu } from "components/shared/SideMenuContainer";
import LoaderButton from "components/shared/LoaderButton";
import Snackbar from "components/shared/Snackbar";
import CertificateHeader from "./CertificateHeader";
import CertificateInfoSection from "./CertificateInfoSection";
import CertificateFilesSection from "./CertificateFilesSection";
import CertificateFileDisplay from "./CertificateFileDisplay";
import CertificateOtherInfoSection from "./CertificateOtherInfoSection";
import CertificateCoveragesSection from "./CertificateCoveragesSection";
import CertificateRequirementSection from "./CertificateRequirementSection";
import Tooltips from "components/shared/Tooltips";
import { SubtitleInline } from "components/shared/Typography";

import { useDispatch, useSelector } from "react-redux";
import { vendorActions, vendorState, certificateState } from "redux/vendorSlice";
import { organizationState } from "redux/organizationSlice";
import { loggedInUserState } from "redux/userSlice";
import { clientClassificationsState } from "redux/clientSlice";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getCertificateFiles, getCertificateSupplementalCoiFiles, getFailedScanFile } from "services/appResources";
import { createFileNameUnique } from "utils/formUtils";
import "./certificate-details.scss";

import useScanData from "hooks/useScanData";
import useCertificateActivities from "hooks/useCertificateActivities";
import useVendorActivities, { CERTIFICATE_ACTIVITIES } from "hooks/useVendorActivities";
import { getNavigationPath, useNavPath, NAVIGATED_FROM } from "constants/routes";
import { validate, hasRequiredDocs } from "utils/certValidationUtils";
import { filterRequestedSupplementalDocFromCertificate } from "utils/certUtils";
import { NavigateNextRounded } from "@mui/icons-material";
import UploadRequestedCOISupplementalDocumentation from "components/VendorDocumentUpload/UploadRequestedCOISupplementalDocumentation";

const menuItems = [
  {
    key: "overview",
    label: "Certificate Overview",
  },
  {
    key: "coverages",
    label: "Coverages",
  },
  {
    key: "documents",
    label: "Documents",
  },
  {
    key: "associations",
    label: "Associations",
  },
];

const scanResultItems = [
  {
    key: "overview",
    label: "Certificate Overview",
  },
  {
    key: "coverages",
    label: "Coverages",
  },
];

const scanStageDisplayText = {
  RECEIVED: "file received",
  SCANNING: "scan started",
  PROCESSING: "saving scan result",
  COMPLETE: "scan completed",
};

export default function CertificateDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId, certificateId, vendorId } = useParams();
  const dispatch = useDispatch();
  const { isAdmin, loggedInUser } = useSelector(loggedInUserState);
  const { vendors } = useSelector(vendorState);
  const certificate = useSelector(certificateState(certificateId));
  console.log('Certificate from Redux for ID', certificateId, ':', certificate);
  const [referrer, setReferrer] = useState(location?.state?.referrer);
  const clientVendorID = certificate?.clientVendorID ? certificate?.clientVendorID : null; //NOTE: vendorId parameter is not actual clientVendorID
  const [clientVendor, setClientVendor] = useState();
  const [certificateData, setCertificateData] = useState({});
  const [certificateFiles, setCertificateFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState();
  const [useClientDefaultProject, setUseClientDefaultProject] = useState(false);
  const [selectedProject, setSelectedProject] = useState();
  const [fileSelected, setFileSelected] = useState({ name: null });
  const [uploadedSupplementalCOIDocumentation, setUploadedSupplementalCOIDocumentation] = useState({});
  const [selectedEmailAddresses, setSelectedEmailAddresses] = useState([]);
  const { selectedClient, userOrgType, selectedAgency } = useSelector(organizationState);
  const projects = useSelector((state) => state.client.projects[selectedClient?.clientID]);
  const NAV_PATH = useNavPath(userOrgType);
  const clientMode = selectedClient?.mode;
  const hasEditPrivileges = isAdmin || (loggedInUser?.role === cognito_groups.ClientPMRole && loggedInUser?.assignedVendors?.includes(clientVendor?.id));
  const hasAddCertificatePrivileges = hasEditPrivileges || (loggedInUser?.role === cognito_groups.Subcontractor && loggedInUser?.assignedVendors?.includes(clientVendor?.id) && certificateId === "create");
  const vendorLabel = getVendorLabel(selectedClient);
  const projectLabel = getProjectLabel(selectedClient);

  const formRef = useRef();
  const activeItem = String(location.hash).replace("#/", "");
  const canSave = ["overview", "coverages", "documents"].includes(activeItem);
  const vendorClassifications = clientMode && clientVendor?.[clientMode === client_mode.category ? "categories" : "projects"];
  const clientClassifications = useSelector(clientClassificationsState);
  const { scanData, getScanData, discardScanData } = useScanData(certificateId, true);
  const hasPending = scanData?.status === "PENDING";
  const hasCompleted = scanData?.status === "COMPLETED";
  const hasFailed = scanData?.status === "FAILED";
  const hasSubmitted = scanData?.status === "SUBMITTED";
  const [viewCompleted, setViewCompleted] = useState(false);
  const [cancelResultAlert, setCancelResultAlert] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [showSendRejectionEmail, setShowRejectionEmail] = useState(false);
  const [rejectionEmailErrors, setRejectionEmailErrors] = useState([]);
  const [rejectionEmailContent, setRejectionEmailContent] = useState("");
  const [initialCoverageRequirements, setInitialCoverageRequirements] = useState();
  const [showProjectCoverages, setShowProjectCoverages] = useState(false)
  const [certificateActivities, addCertificateActivity, addCertificateProjectActivity] = useCertificateActivities(certificate);
  const [, addVendorActivity] = useVendorActivities(clientVendor);
  const [highlightedField, setHighlightedField] = useState(null);
  const [showPdfTypeDialog, setShowPdfTypeDialog] = useState(false);
  const [pendingPdfUpload, setPendingPdfUpload] = useState(null);

  const hasVendorDisabledNotifications = clientVendor?.notificationStatus === "DISABLED";

  // Function to highlight a field on the PDF
  const highlightFieldOnPDF = (fieldName) => {
    setHighlightedField(fieldName);
    // Auto-clear highlight after 3 seconds
    setTimeout(() => setHighlightedField(null), 3000);
  };

  useEffect(() => {
    if (hasCompleted) {
      setShowPending(false);
      setViewCompleted(true);
      if (location.hash !== "#/overview" && location.hash !== "#/coverages") {
        navigate(location.pathname + `#/overview`);
      }
    } else if (hasFailed || hasSubmitted) {
      setShowPending(false);
      setViewCompleted(false);
    }
  }, [hasCompleted, hasFailed, hasSubmitted]);

  const vendorProjects = useMemo(() => {
    if (clientVendor && clientVendor?.projects?.items.length && projects?.length) {
      let selectedVendorProjects;
      if (useClientDefaultProject) {
        selectedVendorProjects = [projects.find((project) => project.isDefault)];
        setSelectedProject(selectedVendorProjects[0]?.id);
      } else {
        selectedVendorProjects = clientVendor.projects.items.map((vendorProject) => ({
          ...(projects.find((project) => vendorProject.projectID === project.id) || {}),
        }));
        if (!selectedProject) {
          setSelectedProject(selectedVendorProjects?.find((project) => project.isDefault)?.id || selectedVendorProjects[0]?.id);
        }
      }
      setCertificateData({
        ...certificateData,
        projectID: selectedProject,
      });
      return selectedVendorProjects;
    }
  }, [clientVendor?.projects?.items, projects?.items?.length, selectedProject]);

  useEffect(() => {
    navigate(location.pathname + `#/${menuItems[0].key}`);

    if (vendorId) {
      const clientVendor = vendors?.find((oneVendor) => oneVendor.vendorID === vendorId || oneVendor.id === vendorId);
      setClientVendor(clientVendor);
    }
    setUseClientDefaultProject(selectedClient?.useDefaultProject);
  }, [vendorId, vendors]);

  const [initialLoad, setInitialLoad] = useState(false);
  const [loadError, setLoadError] = useState(null);

  async function loadInitialData() {
    console.log('Loading certificate data for ID:', certificateId);
    try {
      const result = await dispatch(vendorActions.getCertificate(certificateId));
      console.log('getCertificate result:', result);

      if (result?.error) {
        const errorMessage = result.error?.message || result.payload?.error || 'Failed to load certificate';
        console.error('Error loading certificate:', result.error);
        console.error('Error payload:', result.payload);
        setLoadError(errorMessage);
        setMessage({
          severity: "error",
          text: `Error loading certificate: ${errorMessage}`,
        });
      } else if (!result?.payload && certificateId !== 'create') {
        const errorMessage = 'Certificate not found';
        console.warn(errorMessage);
        setLoadError(errorMessage);
        setMessage({
          severity: "warning",
          text: errorMessage,
        });
      }
      setInitialLoad(true);
    } catch (error) {
      const errorMessage = error?.message || error?.toString() || 'Unknown error loading certificate';
      console.error('Exception loading certificate:', error);
      setLoadError(errorMessage);
      setMessage({
        severity: "error",
        text: `Error loading certificate: ${errorMessage}`,
      });
      setInitialLoad(true);
    }
  }

  useEffect(() => {
    if (certificateId && certificateId !== "create") {
      loadInitialData();
    }
  }, [certificateId]);
  
  useEffect(() => {
  if (clientId && !clientVendor) {
    dispatch(vendorActions.fetchVendors(selectedClient?.clientID));
  } 
}, [selectedClient?.clientID]);

  // Set client Vendor if navigating from the Certificates list
  useEffect(() => {
    console.log('Certificate data useEffect triggered - initialLoad:', initialLoad, 'certificate:', certificate);
    if (initialLoad && certificate) {
      try {
        console.log('Setting certificate data:', certificate);
        setCertificateData({
          ...certificate,
        });

        // Parse initial coverage requirements with error handling
        if (certificate.initialCoverageRequirements && typeof certificate.initialCoverageRequirements === "string") {
          try {
            const initialCoverageRequirements = JSON.parse(certificate.initialCoverageRequirements);
            const filteredInitialCoverageRequirements = {
              ...initialCoverageRequirements,
              ...(certificate?.coverageType?.length && {
                requestedSupplementalDocumentation: filterRequestedSupplementalDocFromCertificate(initialCoverageRequirements?.requestedSupplementalDocumentation, certificate)
              }),
            };
            setInitialCoverageRequirements(filteredInitialCoverageRequirements);
          } catch (parseError) {
            console.warn('Failed to parse initialCoverageRequirements:', parseError);
            setInitialCoverageRequirements(null);
          }
        }

        // Load certificate files with validation
        if (certificate?.acord25File || certificate?.files?.length) {
          try {
            setFileSelected({ name: certificate?.acord25File || certificate.files[0] });
            let files = [];
            if (certificate?.acord25File) {
              files.push({ name: certificate.acord25File, isAcord25: true });
            }
            if (certificate?.files?.length) {
              files = files.concat(certificate.files);
            }
            if (certificate.id && files.length > 0) {
              getCertificateFiles(certificate.id, files, (result) => setCertificateFiles(result));
            }
          } catch (fileError) {
            console.error('Error loading certificate files:', fileError);
            setCertificateFiles([]);
          }
        } else {
          setCertificateFiles([]);
        }

        // Get supplemental documentation if available
        if (certificate?.supplementalCoiFiles) {
          try {
            let supplementalCoiFiles = {};
            if (typeof certificate.supplementalCoiFiles === "string") {
              supplementalCoiFiles = JSON.parse(certificate.supplementalCoiFiles);
            } else {
              supplementalCoiFiles = certificate.supplementalCoiFiles;
            }
            if (certificate.id) {
              getCertificateSupplementalCoiFiles(certificate.id, supplementalCoiFiles, (result) => setUploadedSupplementalCOIDocumentation(result));
            }
          } catch (supplementalError) {
            console.error('Error loading supplemental documentation:', supplementalError);
            setUploadedSupplementalCOIDocumentation({});
          }
        }
      } catch (error) {
        console.error('Error processing certificate data:', error);
        setMessage({
          severity: "error",
          text: `Error loading certificate data: ${error?.message || 'Unknown error'}`,
        });
      }
    }
  }, [certificate, initialLoad]);

  useEffect(() => {
    if (certificateFiles?.length && certificateFiles[certificateFiles.length - 1]?.isNew) {
      setFileSelected({ name: certificateFiles[certificateFiles.length - 1].name });
    }
  }, [certificateFiles]);

  // Load scan files when scan fails or is pending and certificate files are empty
  useEffect(() => {
    if ((hasFailed || hasPending) && scanData?.acord25File && scanData?.certificateID && scanData?.id) {
      // Only load if certificateFiles is empty or doesn't contain the scan file
      const hasFile = certificateFiles?.some(f => f.name === scanData.acord25File);
      if (!hasFile) {
        console.log('Loading scan file from scanning input location:', {
          certificateID: scanData.certificateID,
          scanId: scanData.id,
          fileName: scanData.acord25File,
          status: scanData.status
        });
        try {
          // Use getFailedScanFile to load from scanning input location
          // (works for both failed and pending scans)
          getFailedScanFile(scanData.certificateID, scanData.id, scanData.acord25File, (result) => {
            if (result && result.length > 0) {
              console.log('Successfully loaded scan file:', result);
              setCertificateFiles(result);
              setFileSelected({ name: scanData.acord25File });

              // Update certificateData to include the acord25File so Rescan button shows
              setCertificateData(prevData => ({
                ...prevData,
                acord25File: scanData.acord25File
              }));
            } else {
              console.warn('No file returned from getFailedScanFile');
            }
          });
        } catch (error) {
          console.error('Error loading scan file:', error);
        }
      } else {
        // File already exists in certificateFiles, but ensure certificateData has acord25File
        if (!certificateData.acord25File && scanData.acord25File) {
          console.log('Updating certificateData with existing acord25File');
          setCertificateData(prevData => ({
            ...prevData,
            acord25File: scanData.acord25File
          }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFailed, hasPending, scanData?.acord25File, scanData?.certificateID, scanData?.id]);

  useEffect(() => {
    if (showSendRejectionEmail) {
      const certificateProject = clientClassifications?.find((project) => project.id === certificateData.projectID);
      const result = validate(certificateData, certificateProject?.requiredCoverages, selectedAgency, selectedClient, clientVendor?.vendor);
      setRejectionEmailErrors([...result?.validationErrors]);
      const additionalEmailAddresses = [];
      if (certificateData?.producerEmail && isEmail(certificateData?.producerEmail)) {
        additionalEmailAddresses.push(certificateData?.producerEmail);
      }
      if (selectedClient?.usePrimaryNotificationEmailAddress && selectedClient?.primaryNotificationEmailAddress) {
        additionalEmailAddresses.push(selectedClient?.primaryNotificationEmailAddress);
      }
      setSelectedEmailAddresses(additionalEmailAddresses);
    } else {
      setRejectionEmailErrors([]);
      setRejectionEmailContent("");
    }
  }, [showSendRejectionEmail]);

  async function confirmSendRejectionEmail() {
    let errors = "";
    rejectionEmailErrors.forEach((validationError, index) => (errors += `${index + 1}. ${validationError}\n`));
    if (hasVendorDisabledNotifications) {
      setShowRejectionEmail(false);
      return;
    }
    const createCertResult = await dispatch(
      vendorActions.createRequestCertificate({
        clientVendorID: clientVendor.id,
        clientID: selectedClient.clientID,
        ...(certificateData?.projectID ? { projectID: certificateData?.projectID } : {}),
        ...(certificateData?.initialCoverageRequirements && { initialCoverageRequirements: certificateData?.initialCoverageRequirements }),
      })
    );
    const uploadLink =
      !!selectedClient && !!clientVendor && createCertResult?.payload?.certificateId
        ? window.location.origin + `/vendor/upload/${selectedClient.id}/${clientVendor.vendor.id}/${createCertResult.payload.certificateId}`
        : "";
    const result = await dispatch(
      vendorActions.sendEmailToVendor({
        subject: "Your Certificate Is Not Approved",
        content:
          `Your certificate is not approved for the following reasons:\n` +
          (!!errors ? `${errors}\n\n` : "") +
          (rejectionEmailContent ? `${rejectionEmailContent}.\n\n` : "") +
          (uploadLink ? `Please use <a href=${uploadLink}>this link</a> to upload a new certificate.` : ""),
        vendorName: clientVendor.vendor.name,
        signature: `${selectedClient.name}`,
        recipients: [clientVendor.vendor.email, ...selectedEmailAddresses],
        action: "Custom",
      })
    );
    if (result?.payload?.success) {
      setMessage({
        severity: "success",
        text: `Successfully sent email to `,
      });
      if (uploadLink) {
        addCertificateActivity({
          ...certificateData,
          requestedNewValidCertificateURL: `${window.location.origin}/client/${selectedClient.id}/vendors/${clientVendor.vendor.id}/certificates/${createCertResult?.payload?.certificateId}`,
        });
      }
    }
    setShowRejectionEmail(false);
  }

  function getCertificateStatus(status) {
    setCertificateData({
      ...certificateData,
      status,
    });
  }

  async function onDeleteCertificate() {
    if (!isAdmin) {
      setMessage({
        severity: "error",
        text: `You don't have permission to delete certificate`,
      });
      return;
    }

    try {
      const result = await dispatch(vendorActions.deleteCertificate(certificateData));

      if (result?.payload?.success) {
        // Try to log activity, but don't fail delete if activity logging fails
        try {
          await addVendorActivity({
            action: CERTIFICATE_ACTIVITIES.DELETE_CERTIFICATE,
            clientVendorID: clientVendorID,
            vendor: {
              message: "DELETED certificate",
              details: {
                ...certificate,
              },
            },
          });
        } catch (activityError) {
          console.warn('Failed to log delete activity:', activityError);
        }

        setMessage({
          severity: "success",
          text: `Successfully deleted certificate.`,
        });

        setTimeout(() => {
          navBack();
        }, 1000);
      } else {
        setMessage({
          severity: "error",
          text: `Unable to delete certificate.`,
        });
      }
      return result;
    } catch (error) {
      console.error('Error deleting certificate:', error);
      setMessage({
        severity: "error",
        text: `Error deleting certificate: ${error?.message || 'Unknown error'}`,
      });
      return { success: false, error };
    }
  }

  async function handleCertificateSubmit(updates = {}) {
    // Prevent duplicate submissions
    if (loading) {
      console.log('Submit already in progress, ignoring duplicate request');
      return { success: false };
    }

    setLoading(true);

    try {
      const projectID = updates.projectID || certificateData.projectID || selectedProject;
      const newCertificateData = {
        ...certificateData,
        ...updates,
        ...(clientMode === client_mode.project && projectID ? { projectID } : {}),
        clientVendorID: certificateData.clientVendorID || clientVendor?.id,
        clientID: certificateData.clientID || clientVendor?.clientID,
        coverages: {
          items: formRef.current?.getCoverageData() || [],
        },
      };
      const isRejected = certificateData.id && certificate?.status !== "NOT_APPROVED" && newCertificateData?.status === "NOT_APPROVED";
      const action = certificateData.id ? "update" : "create";
      let createInitialCoverageRequirements;
      if (action === "create") {
        if (selectedProject) {
          createInitialCoverageRequirements = projects.find(project => project.id === selectedProject)?.requiredCoverages
        }
        if (createInitialCoverageRequirements) {
          createInitialCoverageRequirements = { projectID: selectedProject, clientID: selectedClient.clientID, coverages: createInitialCoverageRequirements}
          createInitialCoverageRequirements = JSON.stringify(createInitialCoverageRequirements)
          newCertificateData.initialCoverageRequirements = createInitialCoverageRequirements
        }
      }
      const result = await dispatch(
        vendorActions.createOrUpdateCertificate({
          certificateData: newCertificateData,
          updatedFiles: certificateFiles,
          updatedSupplementalCOIDocumentation: uploadedSupplementalCOIDocumentation,
          coveragesToDelete: formRef.current?.getCoverageDataToDelete() || [],
        })
      );
      if (result?.payload?.success) {
        setMessage({
          severity: "success",
          text: `Successfully ${action}d certificate`,
        });
        if (!certificateData.id) {
          await setCertificateData({
            ...certificateData,
            id: result?.payload?.certificateID,
          });
          addCertificateActivity({ action: "CREATE", id: result?.payload?.certificateID });
        } else {
          addCertificateActivity(newCertificateData, certificateFiles);
        }
        if (certificateFiles?.length && certificateFiles.find((certFile) => certFile.isNew && certFile.isAcord25)) {
          const destination =
            referrer || getNavigationPath(NAV_PATH.CLIENT_VENDOR_DETAILS, { clientId: selectedClient.organizationID, vendorId: clientVendor?.vendor?.id });
          navigate(destination, { state: { fromCertificateCreation: true } });
        } else if (action === "create") {
          // Navigate back after creating certificate without ACORD 25 file
          const destination =
            referrer || getNavigationPath(NAV_PATH.CLIENT_VENDOR_DETAILS, { clientId: selectedClient.organizationID, vendorId: clientVendor?.vendor?.id });
          navigate(destination, { state: { fromCertificateCreation: true } });
        } else if (action === "update" && isRejected && !hasVendorDisabledNotifications) {
          setShowRejectionEmail(true);
        }
        setLoading(false);
        return { success: true };
      } else {
        const errorMessage = result?.error?.message || result?.payload?.error || 'Unknown error occurred';
        console.error(`Failed to ${action} certificate:`, errorMessage);
        setMessage({
          severity: "error",
          text: `Unable to ${action} certificate: ${errorMessage}`,
        });
        setLoading(false);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('Error in handleCertificateSubmit:', error);
      const errorMessage = error?.message || error?.toString() || 'Unknown error';
      setMessage({
        severity: "error",
        text: `Error ${certificateData.id ? 'updating' : 'creating'} certificate: ${errorMessage}`,
      });
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  }
  function onCreateUpdatePathWithId(certificateID) {
    if (String(location.pathname).includes("create")) {
      navigate(String(location.pathname).replace("create", certificateID) + location.hash);
    }
  }
  function handleCertificateInfoUpdate(event) {
    const { name, value } = event.target;
    setCertificateData({
      ...certificateData,
      [name]: value,
    });
  }
  function handleCertificateCoverageUpdate({ id = null, name, value, index: updateIndex }) {
    const currentCoverages = [...(certificateData?.coverages?.items || [])];
    setCertificateData({
      ...certificateData,
      coverages: {
        items: currentCoverages.map((coverage, index) => {
          if (updateIndex === index) {
            return {
              ...coverage,
              [name]: value,
            };
          }
          return coverage;
        }),
      },
    });
  }

  function handleCertificateAddCoverage(defaultCoverage) {
    const coverageData = { ...defaultCoverage };
    delete coverageData.id;
    const updatedCoverages = [...(certificateData?.coverages?.items || []), coverageData];
    setCertificateData({
      ...certificateData,
      coverages: {
        items: updatedCoverages,
      },
    });
  }

  function handleEmailAddressesChange(emails) {
    setSelectedEmailAddresses(emails);
  }

  function handleEmailAddressesRemove(index) {
    setSelectedEmailAddresses([...selectedEmailAddresses.slice(0, index), ...selectedEmailAddresses.slice(index + 1)]);
  }

  const handleFileUpload = (e, newAcord25FileName, { insuranceType, documentType, isSupplementalCoiFile } = {}) => {
    const file =  e.target.files ? e.target.files[0] : e?.dataTransfer?.files[0]
    if(file) {
      // Detect PDF files uploaded to optional docs section
      const isPdfFile = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

      // If uploading a PDF to optional docs section, ask user if it's an ACORD 25 file
      if (isPdfFile && !newAcord25FileName && !isSupplementalCoiFile) {
        console.log('PDF detected in supplemental docs upload - asking user if it is an ACORD 25 file');
        setPendingPdfUpload({ file, event: e });
        setShowPdfTypeDialog(true);
        return; // Wait for user decision
      }

      // Process the file upload directly
      processFileUpload(file, e, newAcord25FileName, { insuranceType, documentType, isSupplementalCoiFile });
    }
  }

  const handleFileDelete = (fileName) => {
    setCertificateFiles(
      certificateFiles.map((certificateFile) =>
        certificateFile.name === fileName ? { ...certificateFile, isDeleted: !certificateFile.isDeleted } : certificateFile
      )
    );
  };

  const handlePdfTypeDecision = (isAcord25) => {
    setShowPdfTypeDialog(false);

    if (pendingPdfUpload) {
      const { file, event } = pendingPdfUpload;

      if (isAcord25) {
        // User confirmed it's an ACORD 25 file - route to AI scanning
        console.log('User confirmed PDF is ACORD 25 - routing to AI scanning pipeline');
        processFileUpload(file, event, true); // Pass true for newAcord25FileName

        setMessage({
          severity: 'success',
          text: 'Processing as ACORD 25 certificate with AI-powered extraction.'
        });
      } else {
        // User said it's not an ACORD 25 file - upload as supplemental doc
        console.log('User indicated PDF is supplemental document - uploading without scanning');
        processFileUpload(file, event, false); // Pass false for newAcord25FileName
      }

      setPendingPdfUpload(null);
    }
  };

  const processFileUpload = (file, e, newAcord25FileName, { insuranceType, documentType, isSupplementalCoiFile } = {}) => {
    const reader = new FileReader();
    reader.onload = function(event) {
      const fileName = String(file.name).replaceAll(" ", "")
      const fileNameUnique = newAcord25FileName ? fileName : createFileNameUnique(
        String(file.name).replaceAll(" ", ""),
        certificateFiles
          .filter(certificateFile => !certificateFile.isAcord25)
          .map(certificateFile => certificateFile.name)
      )
      if (!isSupplementalCoiFile) {
        const newFiles = certificateFiles.map(file => {
          if (newAcord25FileName && file.isAcord25) {
            if (!file.isNew) {
              return {
                ...file,
                isDeleted: true,
                isAcord25: true
              }
            } else {
              return {
                ...file,
                isNew: false,
                isAcord25: true,
                isDeleted: true,
              }
            }
          }
          return { ...file }
        })

        setCertificateFiles([
          ...newFiles,
          {
            content: event.target.result,
            name: fileNameUnique,
            isNew: true,
            isAcord25: !!newAcord25FileName
          }
        ])
      } else {
        setUploadedSupplementalCOIDocumentation({
          ...uploadedSupplementalCOIDocumentation,
          [insuranceType]: {
            ...(uploadedSupplementalCOIDocumentation[insuranceType] || {}),
            [documentType]: {
              content: event.target.result,
              name: fileNameUnique,
              isNew: true,
            },
          },
        });
        setFileSelected({
          name: fileNameUnique,
          insuranceType: insuranceType,
          documentType: documentType,
          isSupplementalCoiFile: true,
        })
      }
    }
    reader.readAsDataURL(file)
  };

  const onDiscardScanResult = async () => {
    if (scanData?.id) {
      setLoading(true);
      await discardScanData();
      // setViewCompleted(false)
      setCancelResultAlert(false);
      setLoading(false);
    }
  };

  const onSubmitScanResult = async () => {
    setLoading(true);

    try {
      // Validate required data before attempting submission
      if (!certificateData?.id) {
        console.error('Cannot submit scan result: Certificate ID is missing', { certificateData });
        setMessage({
          severity: "error",
          text: "Cannot save scan result: Certificate not yet created. Please save the certificate first.",
        });
        setLoading(false);
        return;
      }

      if (!scanData?.id) {
        console.error('Cannot submit scan result: Scan data ID is missing', { scanData });
        setMessage({
          severity: "error",
          text: "Cannot save scan result: Scan data is missing.",
        });
        setLoading(false);
        return;
      }

      if (certificateData.id !== scanData.certificateID) {
        console.error('Cannot submit scan result: Certificate ID mismatch', {
          certificateId: certificateData.id,
          scanCertificateId: scanData.certificateID
        });
        setMessage({
          severity: "error",
          text: "Cannot save scan result: Certificate ID mismatch.",
        });
        setLoading(false);
        return;
      }

      console.log('Submitting scan result:', { certificateId: certificateData.id, scanId: scanData.id });
      const result = await dispatch(vendorActions.submitScanResultToCertificate({ certificateData, scanData }));

      // Log scan submission activity with extraction metadata
      if (result?.payload?.success) {
        const extractionMethodLabels = {
          CLAUDE: 'Claude AI',
          OCR: 'Traditional OCR',
          HYBRID: 'Hybrid (Claude + OCR)',
          MANUAL: 'Manual Entry'
        };

        const modelLabels = {
          CLAUDE_3_5_SONNET_20241022: 'Claude 3.5 Sonnet',
          CLAUDE_3_OPUS_20240229: 'Claude 3 Opus',
          CLAUDE_3_HAIKU_20240307: 'Claude 3 Haiku',
          CLAUDE_3_5_HAIKU_20241022: 'Claude 3.5 Haiku'
        };

        const scanMetadata = {
          extractionMethod: extractionMethodLabels[scanData.extractionMethod] || scanData.extractionMethod || 'Unknown',
          ...(scanData.extractionModel && { model: modelLabels[scanData.extractionModel] || scanData.extractionModel }),
          ...(scanData.extractionConfidence !== null && scanData.extractionConfidence !== undefined && {
            confidence: `${(scanData.extractionConfidence * 100).toFixed(1)}%`
          }),
          ...(scanData.scanConfidence !== null && scanData.scanConfidence !== undefined && {
            overallConfidence: `${(scanData.scanConfidence * 100).toFixed(1)}%`
          }),
          ...(scanData.extractionMetadata && (() => {
            try {
              const metadata = typeof scanData.extractionMetadata === 'string'
                ? JSON.parse(scanData.extractionMetadata)
                : scanData.extractionMetadata;
              return {
                ...(metadata.inputTokens && { inputTokens: metadata.inputTokens.toLocaleString() }),
                ...(metadata.outputTokens && { outputTokens: metadata.outputTokens.toLocaleString() }),
                ...(metadata.totalTokens && { totalTokens: metadata.totalTokens.toLocaleString() }),
                ...(metadata.processingTimeMs && { processingTime: `${(metadata.processingTimeMs / 1000).toFixed(2)}s` }),
              };
            } catch (e) {
              console.warn('Failed to parse extraction metadata:', e);
              return {};
            }
          })()),
          ...(scanData.reprocessedCount > 0 && { reprocessedCount: scanData.reprocessedCount }),
        };

        try {
          await addCertificateActivity({
            id: certificateData.id,
            action: 'SCAN_SUBMITTED',
            scanMetadata
          });
        } catch (activityError) {
          console.warn('Failed to log scan activity:', activityError);
        }

        setMessage({
          severity: "success",
          text: "Scan result saved successfully",
        });
      } else {
        const errorMessage = result?.error?.message || result?.payload?.error || 'Unknown error occurred';
        console.error('Failed to submit scan result:', errorMessage);
        setMessage({
          severity: "error",
          text: `Failed to save scan result: ${errorMessage}`,
        });
      }

      getScanData();
      setLoading(false);
    } catch (error) {
      console.error('Error in onSubmitScanResult:', error);
      const errorMessage = error?.message || error?.toString() || 'Unknown error';
      setMessage({
        severity: "error",
        text: `Error saving scan result: ${errorMessage}`,
      });
      getScanData();
      setLoading(false);
    }
  };

  // Auto-submit scan results when scan completes
  useEffect(() => {
    // Use certificateId from URL params as fallback if local state doesn't have it yet
    const certId = certificateData?.id || certificateId;
    const canAutoSubmit = certId && certId !== 'create';

    if (hasCompleted && !hasSubmitted && scanData?.id && !loading && canAutoSubmit) {
      console.log('Auto-submitting scan result:', { certId, scanId: scanData.id });
      onSubmitScanResult();
    }
  }, [hasCompleted, hasSubmitted, scanData?.id, certificateData?.id, certificateId, loading]);

  const CertificateActions = () =>
    canSave && (
      <LoaderButton
        variant={"contained"}
        onClick={() => handleCertificateSubmit()}
        disabled={loading || hasPending || !hasAddCertificatePrivileges || (certificateId === "create" && !certificateFiles?.length)}
        loading={loading ? loading.toString() : undefined}
        size="small"
        sx={{ ml: "auto", alignSelf: "center" }}
      >
        Save
      </LoaderButton>
    );

  // Removed ScanResultActions - certificates now auto-save without confirmation

  function navBack() {
    const vendorsPath = navBackHelper(certificateId);
    navigate(referrer || vendorsPath);
  }

  function navBackHelper(certificateId) {
    if (certificateId === NAVIGATED_FROM.CERTIFICATES) {
      return getNavigationPath(NAV_PATH.CLIENT_CERTIFICATES, { clientId: clientId });
    } else if (certificateId === NAVIGATED_FROM.PROJECT) {
      return getNavigationPath(NAV_PATH.CLIENT_PROJECTS, { clientId: clientId });
    } else {
      return getNavigationPath(NAV_PATH.CLIENT_VENDOR_DETAILS, { clientId: clientId, vendorId: vendorId });
    }
  }

  function navBackHelperText(certificateId) {
    if (certificateId === NAVIGATED_FROM.CERTIFICATES) {
      return NAVIGATED_FROM.CERTIFICATES;
    } else if (certificateId === NAVIGATED_FROM.PROJECT) {
      return `${projectLabel}s`;
    } else {
      return `${vendorLabel} Detail`;
    }
  }

  return (
    <Box className="certificate-details-wrapper">
      {
        <Button onClick={() => navBack()}>
          <KeyboardArrowLeftIcon />
          Back to {navBackHelperText(vendorId)}
        </Button>
      }

      <Snackbar message={message} setMessage={setMessage} />

      {hasFailed && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography variant="h5" as="span" pl={2}>
            There was an{" "}
            {
              <Tooltips.LightTooltip
                title={
                  <div>
                    <p>File Name: {scanData.acord25File}</p>
                    <p>Error: {JSON.stringify(scanData.scanError)}</p>
                  </div>
                }
              >
                <Link underline={"hover"}>error</Link>
              </Tooltips.LightTooltip>
            }{" "}
            with the last scan. Please try again.
          </Typography>
        </Box>
      )}

      {(hasPending || showPending) && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Loader active inline indeterminate size="small" />
          <Typography variant="h5" as="span" pl={2}>
            {hasPending && `Scanning in progress for Acord25 file... ${(scanData?.scanStage && scanStageDisplayText[scanData.scanStage]) || ""}`}
            {hasCompleted && `Saving scan result to certificate...`}
          </Typography>
        </Box>
      )}

      {showSendRejectionEmail && (
        <Dialog open={showSendRejectionEmail} maxWidth={"xl"} fullWidth={true}>
          <DialogTitle>Send email notification to {vendorLabel}?</DialogTitle>
          <DialogContent>
            {!!rejectionEmailErrors.length && (
              <>
                <Typography variant="body1" sx={{ pb: 1 }}>
                  We found these errors for this certificate
                </Typography>
                <Stack flexDirection="column">
                  {rejectionEmailErrors.map((e, i) => (
                    <Input
                      id={`rejection-email-error-${i}`}
                      value={e}
                      onChange={(e) => {
                        const updatedRejectionErrors = [...rejectionEmailErrors];
                        updatedRejectionErrors[i] = e.target.value;
                        setRejectionEmailErrors(updatedRejectionErrors);
                      }}
                      startAdornment={`${i + 1}. `}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const updatedRejectionErrors = [...rejectionEmailErrors];
                              updatedRejectionErrors.splice(i, 1);
                              setRejectionEmailErrors(updatedRejectionErrors);
                            }}
                            sx={{ float: "right" }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                  ))}
                </Stack>
              </>
            )}

            <Box sx={{ mt: 2 }}>
              <SubtitleInline sx={{ my: 1 }}>Recipient(s)</SubtitleInline>
              <ReactMultiEmail
                placeholder="Other recipients"
                emails={selectedEmailAddresses}
                onChange={(_emails) => handleEmailAddressesChange(_emails)}
                validateEmail={(email) => isEmail(email)}
                getLabel={(email, index) => (
                  <div data-tag key={index}>
                    {email}
                    <span data-tag-handle onClick={() => handleEmailAddressesRemove(index)}>
                      ×
                    </span>
                  </div>
                )}
              />
              <Typography variant="body1" sx={{ pb: 1, mt: "1em !important" }}>
                {!!rejectionEmailErrors.length ? "Additional notes" : "Reason for not approved"}
              </Typography>
              <TextField multiline={true} value={rejectionEmailContent} fullWidth={true} onChange={(e) => setRejectionEmailContent(e.target.value)} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRejectionEmail(false)} disabled={loading}>
              Do Not Send Email
            </Button>
            <Button onClick={confirmSendRejectionEmail} disabled={loading || hasVendorDisabledNotifications}>
              Send
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Removed discard dialog - certificates now auto-save without confirmation */}

      {/* PDF Type Detection Dialog */}
      {showPdfTypeDialog && (
        <Dialog open={showPdfTypeDialog} maxWidth="sm" fullWidth>
          <DialogTitle>PDF File Detected</DialogTitle>
          <DialogContent>
            <DialogContentText>
              You've uploaded a PDF file: <strong>{pendingPdfUpload?.file?.name}</strong>
            </DialogContentText>
            <DialogContentText sx={{ mt: 2 }}>
              Is this an <strong>ACORD 25 Certificate of Insurance</strong>?
            </DialogContentText>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
              <Typography variant="body2" color="primary">
                <strong>Choose "Yes, it's an ACORD 25"</strong> if you want:
              </Typography>
              <Typography variant="body2" sx={{ ml: 2, mt: 0.5 }}>
                • AI-powered automatic data extraction
              </Typography>
              <Typography variant="body2" sx={{ ml: 2 }}>
                • All certificate fields populated automatically
              </Typography>
              <Typography variant="body2" sx={{ ml: 2 }}>
                • Coverage details extracted from the document
              </Typography>
            </Box>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Choose "No, it's a supplemental document"</strong> for:
              </Typography>
              <Typography variant="body2" sx={{ ml: 2, mt: 0.5 }}>
                • Endorsements, declarations, or addendums
              </Typography>
              <Typography variant="body2" sx={{ ml: 2 }}>
                • Supporting documents (no automatic extraction)
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handlePdfTypeDecision(false)} variant="outlined">
              No, it's a supplemental document
            </Button>
            <Button onClick={() => handlePdfTypeDecision(true)} variant="contained" color="primary">
              Yes, it's an ACORD 25
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {certificateId === "create" && (
        <>
          <Box sx={{ float: "right" }}>
            <CertificateActions />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", pl: 2 }}>
            {vendorProjects && (
              <>
                <SubtitleInline sx={{ mt: 2 }}>Select A {projectLabel} *</SubtitleInline>
                <Select
                  size="small"
                  sx={{ minWidth: 120, mb: 2 }}
                  defaultValue={selectedProject}
                  onChange={(e) => {
                    setSelectedProject(e.target.value);
                  }}
                >
                  {vendorProjects.map((vendorProject) => {
                    return (
                      <MenuItem key={"create-project-" + vendorProject.name} value={vendorProject.id}>
                        {vendorProject.name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <Button onClick={() => setShowProjectCoverages(!showProjectCoverages)}>
                  {showProjectCoverages ? "Hide Coverage Requirements" : "Show Coverage Requirements"}
                </Button>
                {selectedProject && vendorProjects.find((project) => project.id === selectedProject) && showProjectCoverages && (
                  <DisplayProjectCoverages
                    project={vendorProjects.find((project) => project.id === selectedProject)}
                    certificateHolder={selectedClient?.name}
                    projectLabel={projectLabel}
                  />
                )}
              </>
            )}
            <CertificateFileDisplay
              supplementalCoiFiles={{ ...uploadedSupplementalCOIDocumentation }}
              key={"cert-file-display"}
              {...{
                certificateFiles,
                fileSelected,
                enableDownload: true,
                handleFileUpload,
                disableUpload: false,
              }}
            />
          </Box>
        </>
      )}
      {certificateId !== "create" && (
        <Dimmer.Dimmable>
          <Dimmer active={hasPending} inverted={true} style={{ backgroundColor: "rgba(255, 255, 255, 0.4)" }} />
          <Stack direction="row" spacing={2} pb={4}>
            <Stack>
              {viewCompleted ? (
                <CertificateFileDisplay
                  supplementalCoiFiles={{ ...uploadedSupplementalCOIDocumentation }}
                  key={"scan-file-display"}
                  {...{
                    certificateFiles: scanData?.files,
                    fileSelected: scanData?.acord25File,
                    enableDownload: false,
                    disableUpload: true,
                  }}
                />
              ) : (
                <CertificateFileDisplay
                  supplementalCoiFiles={{ ...uploadedSupplementalCOIDocumentation }}
                  key={"cert-file-display"}
                  {...{
                    certificateFiles,
                    fileSelected,
                    enableDownload: true,
                    handleFileUpload,
                    disableUpload: false,
                    hasEditPrivileges,
                    highlightedField,
                    fieldConfidence: certificateData?.fieldConfidence || scanData?.fieldConfidence,
                  }}
                />
              )}
            </Stack>
            <Divider orientation={"vertical"} flexItem={true} />
            <Stack sx={{ flexGrow: 1 }}>
              <Typography variant="h5" mr={2}>
                <Button
                  onClick={() => {
                    navigate(getNavigationPath(NAV_PATH.CLIENT_VENDOR_DETAILS, { clientId: clientId, vendorId: clientVendor?.vendor?.id }));
                  }}
                >
                  {clientVendor?.vendor?.name}
                </Button>
                <Box sx={{ float: "right" }}><CertificateActions /></Box>
              </Typography>
              <SideMenu
                items={viewCompleted ? scanResultItems : [...menuItems, ...(isAdmin ? [{ key: "other", label: "Other" }] : [])]}
                vertical={false}
                style={{ marginTop: 0 }}
                className={"major"}
              />
              {canSave &&
                (viewCompleted ? (
                  <CertificateHeader
                    key={"scan-header"}
                    {...{
                      status: certificateData?.status,
                      certificateDate: scanData?.certificateDate,
                      isEdit: false,
                      certificateData,
                    }}
                  />
                ) : (
                  <CertificateHeader
                    key={"cert-header"}
                    {...{
                      status: certificateData?.status,
                      getCertificateStatus,
                      certificateDate: certificateData?.certificateDate,
                      handleCertificateInfoUpdate,
                      isEdit: !hasPending && hasEditPrivileges,
                      certificateData,
                    }}
                  />
                ))}

              {activeItem === menuItems[0].key &&
                (viewCompleted ? (
                  <CertificateInfoSection
                    key={"scan-info-section"}
                    {...{
                      certificateData: scanData,
                      isEdit: false,
                      selectedClient,
                      selectedAgency,
                      vendor: clientVendor?.vendor,
                    }}
                  />
                ) : (
                  <CertificateInfoSection
                    key={"cert-info-section"}
                    {...{
                      handleCertificateInfoUpdate,
                      certificateData,
                      isEdit: !hasPending && hasEditPrivileges,
                      selectedClient,
                      selectedAgency,
                      vendor: clientVendor?.vendor,
                      highlightFieldOnPDF,
                      hasFieldConfidence: !!(certificateData?.fieldConfidence || scanData?.fieldConfidence),
                    }}
                  />
                ))}
              {activeItem === menuItems[1].key && (
                <>
                  {viewCompleted ? (
                    <CertificateCoveragesSection
                      key={"scan-coverage-section"}
                      coverages={scanData?.coverages?.items}
                      // clientCoverages={selectedClient?.clientCoverages?.items}
                      clientMode={clientMode}
                      classifications={vendorClassifications}
                      classificationIDKey={classification_id[selectedClient?.mode]}
                      projectID={clientMode === client_mode.project && certificateData?.projectID}
                      isEdit={false}
                      initialCoverageRequirements={initialCoverageRequirements}
                      acord25FileDate={certificateData?.acord25FileDate}
                      coverageType = {certificateData?.coverageType}
                      certificateType = {certificateData?.certificateType}
                    />
                  ) : (
                    <CertificateCoveragesSection
                      key={"cert-coverage-section"}
                      ref={formRef}
                      coverages={certificateData?.coverages?.items}
                      // clientCoverages={selectedClient?.clientCoverages?.items}
                      clientMode={clientMode}
                      // onUpdate={handleCertificateCoverageUpdate}
                      onAdd={handleCertificateAddCoverage}
                      classifications={vendorClassifications}
                      classificationIDKey={classification_id[selectedClient?.mode]}
                      projectID={clientMode === client_mode.project && certificateData?.projectID}
                      isEdit={!hasPending && hasEditPrivileges}
                      initialCoverageRequirements={initialCoverageRequirements}
                      acord25FileDate={certificateData?.acord25FileDate}
                      coverageType = {certificateData?.coverageType}
                      certificateType = {certificateData?.certificateType}
                      
                    />
                  )}
                </>
              )}
              {activeItem === menuItems[2].key && !viewCompleted && (
                <CertificateFilesSection
                  {...{
                    vendor: clientVendor?.vendor,
                    acord25FileName: certificateData?.acord25File,
                    certificateFiles,
                    handleFileUpload,
                    handleFileDelete,
                    setFileSelected,
                    showVendorLink: true,
                    disableUpload: hasPending || !hasEditPrivileges,
                    vendorLabel: vendorLabel,
                  }}
                >
                  {hasRequiredDocs(initialCoverageRequirements) && (
                    <UploadRequestedCOISupplementalDocumentation
                      fileSelected={fileSelected}
                      showErrorIndicator={false}
                      uploadedSupplementalCOIDocumentation={uploadedSupplementalCOIDocumentation}
                      requestedSupplementalDocumentation={initialCoverageRequirements?.requestedSupplementalDocumentation}
                      setFileSelected={setFileSelected}
                      handleFileUpload={handleFileUpload}
                      disableUpload={hasPending || !hasEditPrivileges}
                    />
                  )}
                </CertificateFilesSection>
              )}
              {activeItem === menuItems[3].key && !viewCompleted && (
                <CertificateRequirementSection
                  {...{
                    vendor: clientVendor?.vendor,
                    certificateID: certificateData?.id,
                    clientVendorID,
                    clientMode,
                    handleCertificateSubmit,
                    // clientCoverages: selectedClient?.clientCoverages?.items,
                    vendorClassifications: vendorClassifications?.items,
                    projectID: clientMode === client_mode.project && certificateData?.projectID,
                    onCreateUpdatePathWithId,
                    addCertificateProjectActivity,
                    hasEditPrivileges,
                    projectLabel: projectLabel,
                    vendorLabel: vendorLabel,
                  }}
                />
              )}
              {activeItem === "other" && !viewCompleted && (
                <CertificateOtherInfoSection
                  {...{
                    certificateData,
                    handleCertificateSubmit,
                    loading,
                    certificateActivities,
                    scanData,
                  }}
                  onDeleteCallback={onDeleteCertificate}
                />
              )}
            </Stack>
          </Stack>
        </Dimmer.Dimmable>
      )}
    </Box>
  );
}
